import { Component, computed, inject, input, OnInit, signal, OnDestroy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BehaviorSubject,
  switchMap,
  shareReplay,
  combineLatest,
  map,
  Subject,
  takeUntil,
  filter,
  debounceTime,
  tap,
} from 'rxjs';
import { StatusModalService } from '../../../../core/services/status-modal.service';
import { WebsocketService } from '../../../../core/services/websocket.service';
import { WebsiteData } from '../../../../shared/models/website-overview.model';
import { FtpUser, FtpUserStatus } from '../../../../shared/models/ftp-users.models';
import { FtpUsersService } from '../../../../core/services/ftp-users.service';
import { Status } from '../../../../shared/models/status-label.model';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SearchFieldComponent } from '../../../../shared/components/search-field/search-field.component';
import { StatusLabelComponent } from '../../../../shared/components/status-label/status-label.component';
import { TableComponent } from '../../../../shared/components/table/table.component';
import { AddFtpUserDialogComponent } from '../../dialogs/add-ftp-user-dialog/add-ftp-user-dialog.component';
import { SnackbarComponent } from '../../../../shared/components/snackbar/snackbar.component';
import { ConfirmationDialogComponent } from '../../../../shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { FtpUserSettingsDialogComponent } from '../../dialogs/ftp-user-settings-dialog/ftp-user-settings-dialog.component';

@Component({
  selector: 'app-ftp-users',
  imports: [
    MatCardModule,
    FontAwesomeModule,
    MatSlideToggleModule,
    SearchFieldComponent,
    TableComponent,
    StatusLabelComponent,
    MatTableModule,
    MatTooltipModule,
    AsyncPipe,
    MatButtonModule,
  ],
  templateUrl: './ftp-users.component.html',
  styleUrl: './ftp-users.component.scss',
})
export class FtpUsersComponent implements OnInit, OnDestroy {
  private ftpUsersService = inject(FtpUsersService);

  private dialog = inject(MatDialog);

  private snackbar = inject(MatSnackBar);

  private statusModalService = inject(StatusModalService);

  private websocketService = inject(WebsocketService);

  websiteData = input.required<WebsiteData>();

  website = computed(() => this.websiteData().website);

  website$ = toObservable(this.website);

  refresh$ = new BehaviorSubject<void>(undefined);

  filters = signal<{ page: number; pageSize: number; searchText?: string }>({
    page: 1,
    pageSize: 10,
  });

  filters$ = toObservable(this.filters);

  allUsersCount = signal(0);

  // filteredUsers$ = combineLatest([this.website$, this.filters$, this.refresh$]).pipe(
  //   debounceTime(300),
  //   switchMap(([website, filters]) =>
  //     combineLatest({
  //       users: this.ftpUsersService.searchFtpUsers(filters.page, website.domain),
  //       filters: of(filters),
  //     }),
  //   ),
  //   tap(({ users: { total } }) => this.allUsersCount.set(total)),
  //   map(({ users, filters }) => {
  //     const records = users.ftpUsers ?? [];

  //     return filters.searchText
  //       ? records.filter((record) =>
  //           record.username.toLowerCase().includes(filters.searchText!.toLowerCase()),
  //         )
  //       : records;
  //   }),
  //   shareReplay(1),
  // );

  filteredUsers$ = combineLatest([this.website$, this.filters$.pipe(debounceTime(300))]).pipe(
    switchMap(([website, filters]) =>
      this.refresh$.pipe(
        switchMap(() => this.ftpUsersService.searchFtpUsers(filters.page, website.domain)),
      ),
    ),
    tap(({ total }) => this.allUsersCount.set(total)),
    map(({ ftpUsers }) => ftpUsers ?? []),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  destroyed$ = new Subject();

  usersColumns = [
    {
      columnDef: 'user',
      header: 'FTP User',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'username',
      header: 'Username',
      cell: (element: FtpUser) => `${element.username}`,
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'directory',
      header: 'Home Directory',
      cell: (element: FtpUser) => element.homeDir,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  ngOnInit(): void {
    this.websocketService
      .listen<{ title: string; body: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(
          ({ title }) =>
            title === 'FTP User Successfully Created' || title === 'FTP User Successfully Toggled',
        ),
      )
      .subscribe(() => {
        this.refresh$.next();
      });
  }

  onSearch(text: string) {
    this.filters.update((f) => ({ ...f, searchText: text }));
  }

  handlePageEvent(event: PageEvent) {
    this.filters.update((f) => ({ ...f, page: event.pageIndex + 1, pageSize: event.pageSize }));
  }

  addFTPUser(): void {
    this.dialog
      .open(AddFtpUserDialogComponent, {
        data: {
          subTitle: this.website().domain,
          website: this.website(),
          systemUser: this.websiteData().systemUser,
          server: this.websiteData().server?.id,
          domain: this.website().domain,
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((data) => this.ftpUsersService.createFtpUser(data)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `FTP User creation started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  toggleUser(event: MatSlideToggleChange, user: FtpUser): void {
    event.source.toggle(); // To prevent visual toggle
    if (user.status === FtpUserStatus.DISABLED) this.enableUser(user);
    else this.disableUser(user);
  }

  enableUser(user: FtpUser): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Enable FTP User',
          paragraphs: [
            `Enabling the FTP User ${user.username} will restore its ability to access to the website files.`,
            `Are you sure you want to enable the FTP User ${user.username}?`,
          ],
          primaryButtonText: 'Enable FTP User',
          icon: ['fas', 'play'],
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.ftpUsersService.toggleFtpUser(user.id, { enable: true })),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `FTP User update started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  disableUser(user: FtpUser): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Disable FTP User',
          paragraphs: [
            `Disabling FTP User ${user.username} will stop all access to the website files.`,
            `Are you sure you want to disable the FTP User ${user.username}?`,
          ],
          primaryButtonText: 'Disable FTP User',
          icon: ['fas', 'pause'],
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.ftpUsersService.toggleFtpUser(user.id, { enable: false })),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `FTP User update started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  openUserSettings(user: FtpUser): void {
    this.dialog
      .open(FtpUserSettingsDialogComponent, {
        data: {
          subTitle: this.website().domain,
          server: this.websiteData().server?.hostname,
          homeDir: user.homeDir,
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((data) => this.ftpUsersService.updateFtpUser(user.id, data)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `FTP User update started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  deleteUser(user: FtpUser): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete FTP User',
          paragraphs: [
            `This action will permanently delete FTP User ${user.username} and revoke its access to all websites`,
            `Are you sure you want to delete FTP User ${user.username}?`,
          ],
          primaryButtonText: 'Delete FTP User',
          type: 'delete',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.ftpUsersService.deleteFtpUser(user.id)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: 'FTP User deletion started!',
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  toStatus(key: FtpUserStatus): Status {
    switch (key) {
      case FtpUserStatus.ACTIVE:
        return Status.ACTIVE;
      case FtpUserStatus.CREATING:
      case FtpUserStatus.DELETING:
      case FtpUserStatus.UPDATING:
        return Status.IN_PROGRESS;
      case FtpUserStatus.DISABLED:
        return Status.DISABLED;
      case FtpUserStatus.DELETED:
        return Status.DEACTIVATED;
      default:
        return Status.ERROR;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }
}
